import { StudentResult } from '@/hooks/types/result';
import { EditorProvider } from '@/providers/editor/EditorProvider';
import { useSession } from '@/providers/session/SessionProvider';
import ClassIcon from '@mui/icons-material/Class';
import ImportContactsIcon from '@mui/icons-material/ImportContacts';
import { Box, Button, Tab, Tabs } from '@mui/material';
import { GridPaginationModel, GridRowSelectionModel } from '@mui/x-data-grid';
import { PageHeader } from '@toolpad/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ExportResultsDialog } from './components/ExportResultsDialog';
import { ResultsPageContainer } from './components/ResultsPageContainer';
import { ResultsPageContent } from './components/ResultsPageContent';
import { ResultsClassroomTab } from './containers/ResultsClassroomTab/ResultsClassroomTab';
import { ResultsHomeworkTab } from './containers/ResultsHomeworkTab/ResultsHomeworkTab';

import { useMultipleStudentResults } from '@/hooks/useStudentResults/useMultipleStudentResults';
import IosShareIcon from '@mui/icons-material/IosShare';
import { ResultTabPanel } from './components/ResultTabPanel';

interface ResultsPageProps {
    initialState?: {
        pagination?: { paginationModel: GridPaginationModel };
    };
}

export const ResultsPage: React.FC<ResultsPageProps> = ({ initialState }) => {
    const { t } = useTranslation();

    const { session } = useSession();

    const compileResultsEnabledSchools = [
        // School Name of schools that can export results
        'HKCS',
    ];

    const compileResultsEnabled = !!session?.user?.teacherProfile?.schools?.some((school) => compileResultsEnabledSchools.includes(school.schoolName));

    // initial state
    const initTab: number = window.history.state?.tab;
    const initPage: number = window.history.state?.page;
    const initPageSize: number = window.history.state?.pageSize;

    if (initPage && initPageSize && typeof initPage === 'number' && typeof initPageSize === 'number') {
        initialState = {
            pagination: {
                paginationModel: {
                    page: initPage,
                    pageSize: initPageSize,
                },
            },
        };
    }

    // ui state
    const [tab, setTab] = React.useState(initTab ?? 0);

    const handleTabChange = (event: React.SyntheticEvent, value: number) => {
        setTab(value);
        history.replaceState({ ...history.state, tab: value }, '');
    };

    const [selectedStudentsResults, setSelectedStudentsResults] = React.useState<StudentResult[] | undefined>(undefined);

    const [classroomRowSelectionModel, setClassroomRowSelectionModel] = React.useState<GridRowSelectionModel>({
        type: 'include',
        ids: new Set(),
    });

    const { loading: loadingStudentResults, fetchStudentResults } = useMultipleStudentResults();

    const handleExportClassroomResults = React.useCallback(async () => {
        if (classroomRowSelectionModel.ids.size <= 0 || loadingStudentResults) {
            return;
        }
        const classroomIds = Array.from(classroomRowSelectionModel.ids).map((id) => id as string);

        const results = await fetchStudentResults('classroom', classroomIds);

        const studentResults = results?.results;

        if (Object.keys(studentResults).length > 0) {
            setSelectedStudentsResults(Object.values(studentResults));
        }
    }, [classroomRowSelectionModel, loadingStudentResults, fetchStudentResults, setSelectedStudentsResults]);

    const TabButtonContainer = ({ style, children }: { style?: React.CSSProperties; children: React.ReactNode }) => <div style={style}>{children}</div>;

    const tabBar = (
        <Tabs
            value={tab}
            onChange={handleTabChange}
            sx={(theme) => ({
                minHeight: 'auto',
                px: 0.75,
                pt: 0.75,
                border: 1,
                borderBottom: 0,

                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,

                borderColor: (theme.vars || theme).palette.divider,
                '& .MuiTab-root': {
                    px: 2,
                    py: 1,
                    fontSize: '0.875rem',
                },

                '& .MuiTabs-flexContainer': {
                    position: 'relative', // Add this for stable indicator positioning
                },
                '& .MuiTabs-indicator': {
                    transition: 'none', // Remove transition for instant positioning
                },
            })}
        >
            <Tab label={t('classroom.title')} icon={<ClassIcon fontSize='small' />} iconPosition='start' />
            <Tab label={t('homework.title')} icon={<ImportContactsIcon fontSize='small' />} iconPosition='start' />

            {tab === 0 && compileResultsEnabled && (
                <TabButtonContainer style={{ marginLeft: 'auto' }}>
                    <Button variant='contained' startIcon={<IosShareIcon />} onClick={handleExportClassroomResults} disabled={classroomRowSelectionModel.ids.size === 0}>
                        {t('result.exportResults.title')}
                    </Button>
                </TabButtonContainer>
            )}
        </Tabs>
    );
    return (
        <ResultsPageContainer>
            <PageHeader title={t('result.title')} breadcrumbs={[{ title: t('result.title') ?? '', path: '/results' }]} />
            <ResultsPageContent>
                <Box sx={{ width: '100%', display: 'flex', flex: 1, flexDirection: 'column' }}>
                    <Box>{tabBar}</Box>
                    <ResultTabPanel value={tab} index={0}>
                        <ResultsClassroomTab
                            initialState={initialState}
                            rowSelectionModel={classroomRowSelectionModel}
                            onRowSelectionModelChange={setClassroomRowSelectionModel}
                            compileResultsEnabled={compileResultsEnabled}
                        />
                    </ResultTabPanel>
                    <ResultTabPanel value={tab} index={1}>
                        <ResultsHomeworkTab />
                    </ResultTabPanel>
                </Box>
            </ResultsPageContent>
            {selectedStudentsResults && (
                <EditorProvider>
                    <ExportResultsDialog open={!!selectedStudentsResults} onClose={() => setSelectedStudentsResults(undefined)} data={selectedStudentsResults} />
                </EditorProvider>
            )}
        </ResultsPageContainer>
    );
};
